import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from './lib/api';
import type { Territory } from './lib/types';
import { distance, hhmm, nowSecondsIn, todayIn } from './lib/format';
import { useOrderActions } from './lib/order-actions';
import { useLiveBoard } from './lib/useLiveBoard';
import { AccountButton, SettingsButton, TopNav, type Section } from './components/TopNav';
import { useAuth } from './components/AuthGate';
import { BillingView } from './components/billing/BillingView';
import { AccountView } from './components/account/AccountView';
import { KpiStrip } from './components/KpiStrip';
import { RouteMap } from './components/RouteMap';
import { RoutesPanel } from './components/RoutesPanel';
import { DispatchSidebar } from './components/DispatchSidebar';
import { RouteDrawer } from './components/RouteDrawer';
import { AuditView } from './components/audit/AuditView';
import { TrialBanner } from './components/TrialBanner';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { BuildingOverlay } from './components/BuildingOverlay';
import { FirstRun } from './components/FirstRun';
import type { OptimizeEvent } from './lib/useLiveBoard';
import { UnassignedPanel } from './components/UnassignedPanel';
import { OrdersView } from './components/OrdersView';
import { TrackingView } from './components/TrackingView';
import { SimulationsView } from './components/SimulationsView';
import { NewOrderDrawer } from './components/orders/NewOrderDrawer';
import { FitInDrawer, type FitPreview } from './components/orders/FitInDrawer';
import { OrderDrawer } from './components/orders/OrderDrawer';
import { SetupView } from './components/setup/SetupView';
import { DatePicker } from '@allrouted/ui/DatePicker';
import { Select } from '@allrouted/ui/Select';
import { DEFAULT_NOTIFICATION_PREFS, useUi } from '@allrouted/ui/Toaster';
import { useRoute } from './lib/url';
import { Resizer, usePaneSize } from '@allrouted/ui/Resizer';

const TERRITORY_KEY = 'allrouted.territory';
const PANEL_KEY = 'allrouted.home.panel';

/**
 * `CJ` -> `CJ's`, `Carlos` -> `Carlos'`.
 *
 * A name already ending in s takes the bare apostrophe; anything else takes
 * `'s`. Small, but these go on a button somebody reads a dozen times an hour
 * and "Carlos's route" reads like a typo to the person whose name it is.
 */
function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}

export function App() {
  const ui = useUi();
  const { me, signOut } = useAuth();

  /*
   * The person's notification preferences, handed to the toaster once they are
   * known. "Mute these" on a toast saves through here; "Notification settings"
   * opens Your profile, where the level and the muted kinds are listed.
   */
  useEffect(() => {
    ui.configureNotifications({
      prefs: me.preferences?.notifications ?? DEFAULT_NOTIFICATION_PREFS,
      save: async (notifications) => {
        await api.savePreferences({ notifications }).catch(() => {});
      },
      openSettings: () => navigateRef.current({ section: 'account', accountTab: 'profile' }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.user?.id]);
  const [territories, setTerritories] = useState<Territory[]>([]);
  /* Distinguishes "none yet" from "not loaded yet", so a new account is not shown a blank board. */
  const [territoriesLoaded, setTerritoriesLoaded] = useState(false);
  /*
   * Which screen, which order, which territory and which day all live in the
   * address bar, so any of it can be linked to. See lib/url.
   */
  const [route, navigate] = useRoute();
  /* For callbacks handed out once, such as the toaster's settings link. */
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const { section, orderRef, setupPage, accountTab } = route;
  /* The four day-to-day sections need a territory; settings, billing and the account do not. */
  const needsTerritory =
    territoriesLoaded && territories.length === 0 && ['home', 'orders', 'tracking', 'simulations'].includes(section);

  /*
   * Territory and date are not in the URL: an operation runs one territory and
   * a dispatcher works today, so carrying both in every link cost readability
   * and bought nothing. The territory is remembered across reloads because
   * re-picking it on every visit is the kind of small friction people notice.
   */
  const [territoryId, setTerritoryId] = useState<string | null>(
    () => localStorage.getItem(TERRITORY_KEY),
  );
  const [date, setDate] = useState<string>(() => todayIn('UTC'));
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [fitOpen, setFitOpen] = useState(false);
  /* Set when Find a fit was opened on an unassigned order rather than an address. */
  const [fitOrder, setFitOrder] = useState<{ id: string; name: string } | null>(null);
  const [fitPreview, setFitPreview] = useState<FitPreview | null>(null);
  /*
   * The actions and unassigned list fold away, because on Home the map is the
   * screen and 288px of it is worth reclaiming when nothing is unassigned.
   * Remembered: on a laptop it stays folded all day.
   */
  const [panelOpen, setPanelOpen] = useState(() => localStorage.getItem(PANEL_KEY) !== '0');
  const panel = usePaneSize('allrouted.home.panelWidth', 288, { min: 220, max: 520 });
  /*
   * The upper bound is the window, not a number somebody picked. 620px was
   * about fifteen rows; a larger fleet could not be shown whole however far the
   * handle was dragged, which reads as the resize being broken rather than as a
   * limit being reached.
   */
  const routes = usePaneSize('allrouted.home.routes', 260, {
    min: 120,
    max: Math.max(320, Math.round(window.innerHeight * 0.8)),
  });
  const togglePanel = useCallback(() => {
    setPanelOpen((open) => {
      localStorage.setItem(PANEL_KEY, open ? '0' : '1');
      return !open;
    });
  }, []);
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [highlightStopId, setHighlightStopId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  /*
   * One technician, alone on the map.
   *
   * Clicking a route used to jump straight to a list of its stops, which
   * answers "what is on this route" while hiding "where does it go" -- and the
   * map is the reason to click a route on a map. Focusing draws that route by
   * itself; the list is one button away when it is wanted.
   */
  const [focusRouteId, setFocusRouteId] = useState<string | null>(null);
  /*
   * The route build in progress, if this person started one: from the click
   * until the live feed says that request finished or failed. `requestId` is
   * null for the moment between the click and the API answering.
   */
  const [building, setBuilding] = useState<{
    requestId: string | null;
    startedAt: number;
    orders: number;
    vehicles: number;
    hidden: boolean;
  } | null>(null);
  const optimizing = building !== null;
  /*
   * A small day can finish before the request that started it has answered.
   * Outcomes are kept here briefly, so the answer can find its own.
   */
  const earlyOutcomes = useRef(new Map<string, OptimizeEvent>());
  const buildingRef = useRef(building);
  buildingRef.current = building;
  const [clock, setClock] = useState('');
  /* Routes hidden from the map. Local: a view preference, not shared state. */
  const [hiddenRoutes, setHiddenRoutes] = useState<Set<string>>(() => new Set());

  /** The live feed says a build ended: close the overlay and say how it went. */
  const finishBuild = (event: OptimizeEvent) => {
    setBuilding(null);
    if (event.type === 'failed') {
      ui.notify('error', 'Could not build routes', event.error);
      return;
    }
    const s = event.stats;
    ui.notify(
      'ok',
      'Routes built',
      s
        ? `${s.routes} route${s.routes === 1 ? '' : 's'}, ${s.assigned} stop${s.assigned === 1 ? '' : 's'} assigned` +
            (s.unassigned ? `, ${s.unassigned} could not be placed` : '') +
            ` in ${Math.max(1, Math.round(s.elapsedMs / 1000))}s.`
        : 'The board shows the new plan.',
    );
  };
  function onOptimizeEvent(event: OptimizeEvent) {
    const current = buildingRef.current;
    if (!current) return;
    if (current.requestId === null) {
      earlyOutcomes.current.set(event.requestId, event);
      return;
    }
    if (current.requestId === event.requestId) finishBuild(event);
  }

  const { board, connection, refresh } = useLiveBoard(territoryId, date, onOptimizeEvent);

  /*
   * Reload the territory list. Called on mount, and again whenever the
   * territories page adds, renames or removes one -- the header picker is a
   * second view of the same list, and a stale copy shows a placeholder where a
   * name should be.
   */
  const reloadTerritories = useCallback(
    async (settle = false) => {
      try {
        const { territories: list } = await api.territories();
        setTerritories(list);
        setTerritoriesLoaded(true);
        if (!settle) return;
        /* A remembered territory wins, as long as it still exists. */
        const remembered = list.find((t) => t.id === localStorage.getItem(TERRITORY_KEY));
        const chosen = remembered ?? list[0];
        if (!chosen) {
          /*
           * Nothing here yet. A territory remembered from another account
           * signed in on this browser must not linger, or every request for
           * the board is refused as somebody else's.
           */
          setTerritoryId(null);
          localStorage.removeItem(TERRITORY_KEY);
          return;
        }
        setTerritoryId(chosen.id);
        localStorage.setItem(TERRITORY_KEY, chosen.id);
        setDate(todayIn(chosen.timezone));
      } catch (err) {
        ui.notify(
          'error',
          'Could not load territories',
          err instanceof ApiError ? err.message : String(err),
        );
      }
    },
    [ui],
  );

  useEffect(() => {
    // Settle on mount: this is the initial resolution of an unspecified URL.
    void reloadTerritories(true);
  }, [reloadTerritories]);

  /*
   * Stable, because an inline arrow here is a new function on every render of
   * this shell -- and the board re-renders constantly, patching itself from the
   * live feed. A child that puts such a prop in a dependency list sees its
   * effects re-run forever. Setup's territory form did exactly that: it closed
   * itself the instant anything else on screen moved.
   */
  const territoriesChanged = useCallback(() => {
    void reloadTerritories();
  }, [reloadTerritories]);

  const timezone = board?.territory.timezone ?? territories[0]?.timezone ?? 'UTC';

  useEffect(() => {
    const tick = () => setClock(hhmm(nowSecondsIn(timezone)));
    tick();
    const id = setInterval(tick, 15_000);
    return () => clearInterval(id);
  }, [timezone]);

  const selectSection = useCallback(
    (next: Section) => navigate({ section: next }),
    [navigate],
  );

  /* Arriving from the link in the verification email. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') !== '1') return;
    ui.notify('ok', 'Email confirmed', 'Welcome to AllRouted. Your free trial has started.');
    params.delete('verified');
    params.delete('welcome');
    const q = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (q ? `?${q}` : ''));
  }, [ui]);

  /*
   * Which screens people use, for the activity log. Once per change of
   * section, not per render or per refresh.
   */
  useEffect(() => {
    if (me.user) void api.reportView(section);
  }, [section, me.user]);

  /*
   * An owner whose trial has ended lands on Billing rather than on a board
   * that refuses every change. Once, on arrival: after that they can look
   * around, and the banner says why nothing saves.
   */
  const sentToBilling = useRef(false);
  useEffect(() => {
    if (sentToBilling.current) return;
    sentToBilling.current = true;
    const lapsed = me.billing?.status === 'trial_expired' || me.billing?.status === 'canceled';
    if (lapsed && me.user?.role === 'owner' && section === 'home') navigate({ section: 'billing' });
  }, [me, section, navigate]);

  /*
   * Stable, because OrdersView builds its loader from it. An inline arrow here
   * gives the loader a new identity every render and the screen refetches in a
   * loop.
   */
  /*
   * Switching territory, from the header picker or from the territories page.
   * Both land here so the two can never disagree about which one is live.
   */
  const selectTerritory = useCallback(
    (id: string) => {
      setTerritoryId(id);
      localStorage.setItem(TERRITORY_KEY, id);
      // An order reference means nothing in another territory's day.
      navigate({ orderRef: null });
      setSelectedRouteId(null);
      setDrawerOpen(false);
    },
    [navigate],
  );

  const selectOrder = useCallback(
    (ref: string | null, auto = false) => navigate({ orderRef: ref }, { replace: auto }),
    [navigate],
  );

  /*
   * What the map actually hides: whatever the dispatcher switched off, plus
   * every other route while one is focused. Kept separate from `hiddenRoutes`
   * so leaving focus restores exactly the eye toggles they had set.
   */
  const hiddenOnMap = useMemo(() => {
    if (!focusRouteId || !board) return hiddenRoutes;
    /* Only real routes are drawn, so only real routes can be hidden. */
    return new Set(
      board.routes
        .map((r) => r.id)
        .filter((id): id is string => Boolean(id) && id !== focusRouteId),
    );
  }, [focusRouteId, hiddenRoutes, board]);

  const focusedRoute = useMemo(
    /*
     * Nothing focused is no route -- not the first lane whose id is also null.
     * A vehicle with no route has a null id, so a bare comparison "focused"
     * the first idle or switched-off van on every page load.
     */
    () => (focusRouteId ? (board?.routes.find((r) => r.id === focusRouteId) ?? null) : null),
    [board, focusRouteId],
  );

  const selectedRoute = useMemo(
    /* Lanes are keyed by route, or by vehicle where there is no route yet. */
    () =>
      board?.routes.find(
        (r) =>
          (r.id ?? `v:${r.vehicleId}`) === selectedRouteId &&
          /* A vehicle that is off, with nothing on it, has nothing to show on the map -- and switching it off clears it. */
          !(r.available === false && r.stops.length === 0),
      ) ?? null,
    [board, selectedRouteId],
  );

  const openRoute = useCallback((routeId: string) => {
    setSelectedRouteId(routeId);
    setHighlightStopId(null);
    setFocusRouteId((current) => (current === routeId ? null : routeId));
  }, []);

  /**
   * A stop chip on the timeline sends the map to it.
   *
   * Three behaviours in as many iterations, and the third is the right one.
   * Clicking `2.1` opened the whole route, which answered a question nobody
   * asked; then it opened the order's drawer, which answered a later one and
   * covered the board doing it. What a dispatcher wants first from a stop
   * number is *where* -- the map pans to the pin, draws it large, and the
   * bubble beside it carries the customer, the time and the address, with a
   * button through to the order for when that is what they were after.
   *
   * No drawer, so the route stays readable behind it.
   */
  const openStop = useCallback((routeId: string, stopId: string) => {
    setSelectedRouteId(routeId);
    setHighlightStopId(stopId);
  }, []);

  /** The whole day for one technician, from the table's own column. */
  const openRouteDrawer = useCallback((routeId: string) => {
    setSelectedRouteId(routeId);
    setHighlightStopId(null);
    setDrawerOpen(true);
  }, []);

  /**
   * Rebuild the day, or one driver's part of it.
   *
   * `vehicleIds` empty is the whole fleet, which is what the toolbar button
   * means. Scoped, the solver re-plans only those vehicles and only the work
   * already on them, and everyone else's routes are left exactly as they are.
   */
  const optimize = useCallback(async (vehicleIds: string[] = []) => {
    if (!territoryId || !board) return;

    const started = board.routes.some((r) => r.actualStart !== null);
    if (started) {
      const ok = await ui.confirm({
        title: 'Replan the rest of the day?',
        body:
          'Completed and in-progress visits stay where they are. Every stop that has not been started may move to a different technician or time.',
        confirmLabel: 'Replan',
      });
      if (!ok) return;
    }

    /*
     * Assignments a person made, and what a replan should do with them.
     *
     * A rebuild deletes every unlocked route, so a stop dragged onto a van goes
     * back wherever the solver prefers -- silently, which is how a dispatcher
     * learns not to trust the board. Three answers, because two of them are
     * right depending on why they are replanning: keep the hand-placed work and
     * re-plan around it, or hand it all back to the solver.
     */
    const byHand = board.routes.flatMap((r) => (r.stops ?? []).filter((s) => s.placedByHand));
    let keepManual = true;
    if (byHand.length > 0) {
      const answer = await ui.choose({
        title: `${byHand.length} stop${byHand.length === 1 ? '' : 's'} placed by hand`,
        body:
          `${byHand.map((s) => s.orderName).slice(0, 4).join(', ')}` +
          `${byHand.length > 4 ? ` and ${byHand.length - 4} more` : ''}. ` +
          'Keeping them pins each to the technician you chose and re-plans everything else ' +
          'around it. Including them lets the solver move them wherever it likes.',
        confirmLabel: 'Keep them where they are',
        altLabel: 'Include them in the replan',
      });
      if (answer === 'cancel') return;
      keepManual = answer === 'confirm';
    }

    /* A vehicle that is off is not planned for, so it is not counted either. */
    const vehicleCount = vehicleIds.length || board.routes.filter((r) => r.available !== false).length;
    const orderCount =
      board.routes.reduce((n, r) => n + (r.stops?.length ?? 0), 0) + board.unassigned.length;
    earlyOutcomes.current.clear();
    setBuilding({ requestId: null, startedAt: Date.now(), orders: orderCount, vehicles: vehicleCount, hidden: false });
    try {
      const { requestId } = await api.optimize(territoryId, date, { timeLimit: 25, keepManual, vehicleIds });
      const early = earlyOutcomes.current.get(requestId);
      earlyOutcomes.current.clear();
      if (early) finishBuild(early);
      else setBuilding((b) => (b ? { ...b, requestId } : b));
    } catch (err) {
      setBuilding(null);
      ui.notify('error', 'Could not build routes', err instanceof ApiError ? err.message : String(err));
    }
  }, [territoryId, date, board, ui]);

  /**
   * Hand the day to the drivers.
   *
   * This used to say "the driver app is not connected, so there is nowhere to
   * send it" -- which was never true of this installation. The operator's own
   * app reads `toa/routes`, and that surface only returns a plan once it is
   * `active`. Dispatching *is* that status change; nothing is pushed anywhere,
   * which is why there was nothing obvious to point at.
   *
   * Confirmed rather than immediate. A day that is already out means
   * technicians are looking at it, and a replan after that moves work somebody
   * has already been told about.
   */
  const dispatchRoutes = useCallback(async () => {
    if (!territoryId || !board?.plan) return;

    const already = board.plan.status === 'active';
    const ok = await ui.confirm({
      title: already ? 'Withdraw the day?' : 'Dispatch the day to drivers?',
      body: already
        ? 'Drivers stop seeing these routes until it is dispatched again. Work already completed is untouched.'
        : `${board.routes.filter((r) => r.stops.length > 0).length} routes become visible to the driver app. You can still replan afterwards, but technicians may already have seen what changed.`,
      confirmLabel: already ? 'Withdraw' : 'Dispatch',
    });
    if (!ok) return;

    try {
      await api.setPlanStatus(territoryId, board.plan.id, already ? 'planned' : 'active');
      ui.notify(
        'ok',
        already ? 'Withdrawn' : 'Dispatched',
        already
          ? 'The driver app no longer returns these routes.'
          : 'The driver app can now see the day.',
      );
      await refresh();
    } catch (err) {
      ui.notify(
        'error',
        'Could not dispatch',
        err instanceof ApiError ? err.message : String(err),
      );
    }
  }, [territoryId, board, ui, refresh]);

  /*
   * Moving a stop retimes both affected routes on the server, so the board is
   * refetched rather than patched: sequences, planned times and projections all
   * shift, and reconstructing that client-side would be a second implementation
   * of the timetable to keep in step.
   */
  /*
   * An order picked up in the unassigned panel, on its way to a lane.
   *
   * The two panels are siblings, so the press is recorded here and the routes
   * panel adopts it -- one implementation of dragging and dropping rather than
   * two that almost agree.
   */
  /*
   * The order opened from a stop in the route drawer.
   *
   * The name is carried alongside the id so the drawer has a title before the
   * order has loaded -- the stop already knows it, and a heading that says
   * "Order" for half a second reads as the wrong thing having opened.
   */
  const [openOrder, setOpenOrder] = useState<{ id: string; name: string } | null>(null);

  /*
   * The fleet, for the order's pinned-vehicle field.
   *
   * Taken from the board rather than fetched: it already lists every active
   * vehicle, idle ones included, because those are lanes work can be dropped on.
   * One less request behind a drawer that opens on a click.
   */
  const orderDrawerVehicles = useMemo(
    () =>
      (board?.routes ?? [])
        /* Not one that is switched off or off today: work cannot be given to it. */
        .filter((r) => r.available !== false || r.stops.length > 0)
        .map((r) => ({ id: r.vehicleId, name: r.vehicleName })),
    [board],
  );

  const [pendingDrag, setPendingDrag] = useState<{
    orderId: string;
    label: string;
    x: number;
    y: number;
  } | null>(null);

  /**
   * Place an order the solver would not take.
   *
   * Overruling it deliberately: the route is retimed on the spot, and if the day
   * no longer fits the board says so. Which is the point -- a dispatcher who
   * knows the visit is quick gets to decide, and then gets to see the cost.
   */
  const assignOrder = useCallback(
    async (orderId: string, target: string, toSequence: number) => {
      if (!territoryId) return;
      try {
        await api.assignOrder(territoryId, date, orderId, target, toSequence);
        await refresh();
        ui.notify('ok', 'Order placed', 'The route was retimed — check it for new warnings.');
      } catch (err) {
        ui.notify(
          'error',
          'Could not place that order',
          err instanceof ApiError ? err.message : String(err),
        );
        await refresh();
      }
    },
    [territoryId, date, refresh, ui],
  );

  /**
   * Move a stop in time without moving it anywhere else.
   *
   * The lane is re-timed around it, so everything after it shifts too -- which
   * is the point: holding a four o'clock job until half four pushes the rest of
   * the afternoon, and seeing that is how a dispatcher decides whether to.
   */
  const holdStop = useCallback(
    async (stopId: string, notBeforeSec: number | null) => {
      if (!territoryId || !board) return;
      const route = board.routes.find((r) => r.stops.some((s) => s.id === stopId));
      const stop = route?.stops.find((s) => s.id === stopId);
      if (!stop) return;
      try {
        await api.holdStop(territoryId, stopId, notBeforeSec, stop.sequence);
        await refresh();
        ui.notify(
          'ok',
          notBeforeSec === null
            ? `${stop.orderName} released`
            : `${stop.orderName} held until ${hhmm(notBeforeSec)}`,
          notBeforeSec === null
            ? 'It will be served as soon as the technician reaches it.'
            : 'The rest of the lane was re-timed around it.',
        );
      } catch (err) {
        ui.notify(
          'error',
          'Could not change that stop',
          err instanceof ApiError ? err.message : String(err),
        );
        await refresh();
      }
    },
    [territoryId, board, refresh, ui],
  );

  const moveStop = useCallback(
    async (stopId: string, toRouteId: string, toSequence: number) => {
      if (!territoryId) return;
      try {
        await api.moveStop(territoryId, stopId, toRouteId, toSequence);
        await refresh();
        ui.notify('ok', 'Stop moved', 'Both routes were retimed.');
      } catch (err) {
        const message = err instanceof ApiError ? err.message : String(err);
        ui.notify('error', 'Could not move that stop', message);
        await refresh();
      }
    },
    [territoryId, refresh, ui],
  );

  /**
   * Size the routes panel to show every route at once.
   *
   * The panel scrolls, and a dispatcher looking for the van with a gap in it
   * cannot see a fleet that does not fit -- scrolling a thirteen-row list to
   * compare rows four and eleven is not reading a board, it is remembering one.
   *
   * Measured from the grid rather than computed from a row height: the header
   * row, the totals and the panel chrome all take space, and a formula would be
   * wrong the moment any of them changed.
   */
  const fitRoutes = useCallback(() => {
    const grid = document.querySelector<HTMLElement>('.rp-grid');
    const body = document.querySelector<HTMLElement>('.rp-body');
    if (!grid || !body) return;
    /* What the panel spends on everything that is not the grid. */
    const chrome = body.parentElement
      ? body.parentElement.getBoundingClientRect().height - body.getBoundingClientRect().height
      : 0;
    /* Leave the map something to be: never more than three quarters of the window. */
    routes.setSize(
      Math.min(grid.scrollHeight + chrome + 12, Math.round(window.innerHeight * 0.75)),
    );
  }, [routes]);

  /**
   * A working day dragged to a new start or end.
   *
   * `vehicleId` null is the whole fleet, which is the ruler being dragged: one
   * request, one audit entry, one transaction -- rather than thirteen that can
   * half-fail.
   *
   * Re-plans afterwards, scoped to whoever was dragged -- see the body for why
   * both halves of that are necessary.
   */
  const shiftDay = useCallback(
    async (vehicleId: string | null, edge: 'start' | 'end', seconds: number) => {
      if (!territoryId) return;
      try {
        const body =
          edge === 'start'
            ? { vehicleId: vehicleId ?? undefined, shiftStart: seconds }
            : { vehicleId: vehicleId ?? undefined, shiftEnd: seconds };
        const result = await api.setDayShift(territoryId, date, body);
        await refresh();

        /*
         * Nothing re-plans.
         *
         * Changing the hours makes room; it does not rearrange the work, and an
         * automatic solve on release took the board out from under whoever was
         * still adjusting it. Widening a lane so a stop can be dragged into it
         * is the common case, and re-planning is exactly the wrong response to
         * it -- the solver would fill the space itself and there would be
         * nothing left to drag.
         *
         * Rebuilding is offered on the toast instead, for the times it is
         * wanted.
         */
        /*
         * Named, not "this driver".
         *
         * Half a dozen of these stack up while somebody works quickly through
         * the board, and "Rebuild this driver" on four of them at once says
         * nothing about which four drivers. The name is the only part that
         * tells them apart, so it goes in the heading and on the button.
         */
        const who = vehicleId
          ? (board?.routes.find((r) => r.vehicleId === vehicleId)?.vehicleName ?? 'this driver')
          : null;

        ui.notify(
          'ok',
          who ? `Hours changed for ${who}` : `Hours changed for ${result.vehicles} vehicles`,
          'The lane is longer; the work on it has not moved. Drag stops into the new space, ' +
            (who ? `or rebuild ${possessive(who)} route to let the solver use it.` : 'or rebuild the fleet to let the solver use it.'),
          {
            /*
             * Scoped to whoever was dragged. Changing one technician's hours is
             * not a reason to redraw twelve other people's days, and the label
             * says whose it will be -- a button called "Rebuild" that quietly
             * replans the whole board is how somebody loses an afternoon's
             * arranging. The fleet ruler passes no vehicle and then it is the
             * fleet, which is what that drag meant.
             */
            label: who ? `Rebuild ${possessive(who)} route` : 'Rebuild the fleet',
            run: () => void optimize(vehicleId ? [vehicleId] : []),
          },
        );
      } catch (err) {
        ui.notify(
          'error',
          'Could not change the hours',
          err instanceof ApiError ? err.message : String(err),
        );
        await refresh();
      }
    },
    /* `board` is read for the driver's name; `optimize` by the toast's action. */
    [territoryId, date, refresh, ui, optimize, board],
  );

  /*
   * The two destructive actions the map's bubble offers, shared with the order
   * drawer and the orders map so the wording and the confirmation cannot
   * differ by which pixel was clicked. The board is refetched rather than
   * patched: a deleted stop is not something the live stream will tell us
   * about in a form we could apply.
   */
  const orderActions = useOrderActions(territoryId);
  const unassignFromMap = useCallback(
    async (orderId: string, orderName: string) => {
      if (await orderActions.unassign(orderId, orderName, date)) await refresh();
    },
    [orderActions, date, refresh],
  );
  const deleteFromMap = useCallback(
    async (orderId: string, orderName: string) => {
      if (await orderActions.remove(orderId, orderName)) await refresh();
    },
    [orderActions, refresh],
  );

  const toggleHidden = useCallback((routeId: string) => {
    setHiddenRoutes((current) => {
      const next = new Set(current);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });
  }, []);

  /**
   * Every route on the map, or none of them.
   *
   * Hiding all and showing one is how a dispatcher reads a single driver's day
   * on a board of thirteen, so the bulk control is the one that saves the
   * clicks. Idle lanes are skipped: they have no route and nothing drawn.
   */
  const hideAll = useCallback(
    (hidden: boolean) => {
      setHiddenRoutes(
        hidden
          ? new Set((board?.routes ?? []).map((r) => r.id).filter((id): id is string => Boolean(id)))
          : new Set(),
      );
    },
    [board],
  );

  const toggleLock = useCallback(
    async (routeId: string, locked: boolean) => {
      if (!territoryId) return;
      try {
        await api.lockRoute(territoryId, routeId, locked);
        await refresh();
        ui.notify(
          'ok',
          locked ? 'Route locked' : 'Route unlocked',
          locked ? 'A replan will leave this route alone.' : undefined,
        );
      } catch (err) {
        ui.notify('error', 'Could not change the lock', err instanceof ApiError ? err.message : String(err));
      }
    },
    [territoryId, refresh, ui],
  );

  /**
   * Lock or unlock the whole day.
   *
   * The reason this exists is replanning scope: a rebuild leaves locked routes
   * untouched, so locking everything and unlocking one driver is how a
   * dispatcher re-plans that driver alone. Doing it here rather than in a loop
   * over routes keeps it to one request and one audit entry.
   */
  const lockAll = useCallback(
    async (locked: boolean) => {
      if (!territoryId) return;
      try {
        const result = await api.setDayLock(territoryId, date, locked);
        await refresh();
        ui.notify(
          'ok',
          locked ? 'Every route locked' : 'Every route unlocked',
          result.changed === 0
            ? 'Nothing needed changing.'
            : locked
              ? `${result.changed} route${result.changed === 1 ? '' : 's'} locked. ` +
                'A replan will leave them as they are — unlock the one you want redone.'
              : `${result.changed} route${result.changed === 1 ? '' : 's'} unlocked.`,
        );
      } catch (err) {
        ui.notify(
          'error',
          'Could not change the locks',
          err instanceof ApiError ? err.message : String(err),
        );
      }
    },
    [territoryId, date, refresh, ui],
  );

  /*
   * Reverse the pending tail of a route, as the incumbent's action bar does.
   *
   * Confirmed first because it moves every remaining stop at once and there is
   * no undo: a dispatcher who meant to reverse a different route would have to
   * rebuild this one by hand.
   */
  const reverseRoute = useCallback(
    async (routeId: string) => {
      if (!territoryId) return;
      const route = board?.routes.find((r) => r.id === routeId);
      const ok = await ui.confirm({
        title: 'Reverse this route?',
        body: `${route?.vehicleName ?? 'This route'} will run its remaining stops in the opposite order. Stops already visited stay where they are.`,
        confirmLabel: 'Reverse',
      });
      if (!ok) return;

      try {
        const result = await api.reverseRoute(territoryId, routeId);
        await refresh();
        ui.notify(
          'ok',
          result.reversed >= 2 ? 'Route reversed' : 'Nothing to reverse',
          result.reversed >= 2
            ? `${result.reversed} pending stops now run in the opposite order.`
            : 'This route has fewer than two stops still to visit.',
        );
      } catch (err) {
        ui.notify('error', 'Could not reverse the route', err instanceof ApiError ? err.message : String(err));
      }
    },
    [territoryId, board, refresh, ui],
  );

  const focusOrder = useCallback(
    (orderId: string) => {
      const order = board?.unassigned.find((o) => o.id === orderId);
      if (!order) return;
      ui.notify(
        'info',
        order.name,
        order.lat === null
          ? 'This order has no location, so it cannot be routed.'
          : 'Not on any route today. Replan or extend a shift to fit it in.',
      );
    },
    [board, ui],
  );

  return (
    <div className="shell">
      <div className="main">
        <header className="topbar">
          <TopNav section={section} onSelect={selectSection} />

          <span className="topbar-rule" aria-hidden="true" />

          <Select
            value={territoryId}
            placeholder="Territory"
            options={territories.map((t) => ({ value: t.id, label: t.name }))}
            onChange={selectTerritory}
            footer={(close) => (
              <button
                type="button"
                className="pop-item"
                onClick={() => {
                  close();
                  navigate({ section: 'fleet' });
                }}
              >
                <span>Territory settings</span>
              </button>
            )}
          />

          <SettingsButton
            active={section === 'fleet'}
            onClick={() => navigate({ section: 'fleet' })}
          />

          <DatePicker value={date} today={todayIn(timezone)} onChange={setDate} />

          <div className="spacer" />

          <span className="clock">{clock}</span>

          <span className="livedot" data-state={connection}>
            <i />
            {connection === 'live' ? 'Live' : connection === 'error' ? 'Reconnecting' : 'Connecting'}
          </span>

          <button type="button" className="btn" onClick={() => void refresh()}>
            Refresh
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void optimize()}
            disabled={optimizing || !board}
          >
            {optimizing ? <span className="spinner" /> : null}
            {optimizing ? 'Building Routes' : 'Build Routes'}
          </button>

          <AccountButton
            email={me.user?.email ?? 'Integration key'}
            accountName={me.account.name}
            role={me.user?.role ?? 'api key'}
            onGo={(next) => navigate({ section: next })}
            onSignOut={() => void signOut()}
          />
        </header>

        {me.impersonation ? (
          <ImpersonationBanner me={me} />
        ) : (
          <TrialBanner me={me} onBilling={() => navigate({ section: 'billing' })} />
        )}

        {needsTerritory ? (
          <FirstRun
            accountName={me.account.name}
            onCreated={(id) => {
              void reloadTerritories().then(() => {
                selectTerritory(id);
                navigate({ section: 'fleet', setupPage: 'depots' });
              });
            }}
          />
        ) : null}

        {building && !building.hidden ? (
          <BuildingOverlay
            startedAt={building.startedAt}
            orders={building.orders}
            vehicles={building.vehicles}
            onHide={() => setBuilding((b) => (b ? { ...b, hidden: true } : b))}
          />
        ) : null}

        {section === 'home' && territoriesLoaded && !needsTerritory ? (
          <div className="workspace">
            <KpiStrip board={board} />
            <div
              className="split"
              data-panel={panelOpen ? 'open' : 'closed'}
              style={
                panelOpen
                  ? { gridTemplateColumns: `${panel.size}px auto 1fr` }
                  : { gridTemplateColumns: '0 auto 1fr' }
              }
            >
              <button
                type="button"
                className="panel-toggle"
                style={{ left: panelOpen ? panel.size : 0 }}
                onClick={togglePanel}
                aria-expanded={panelOpen}
                aria-label={panelOpen ? 'Hide the actions panel' : 'Show the actions panel'}
                title={panelOpen ? 'Hide panel' : 'Show panel'}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                  <path
                    d={panelOpen ? 'M7.5 2L3.5 6l4 4' : 'M4.5 2l4 4-4 4'}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div className="left-stack">
                <DispatchSidebar
                  board={board}
                  optimizing={optimizing}
                  onAddOrder={() => setNewOrderOpen(true)}
                  onFindFit={() => {
                    setFitOrder(null);
                    setFitOpen(true);
                  }}
                  onBuildRoutes={() => void optimize()}
                  onDispatchRoutes={() => void dispatchRoutes()}
                />
                <UnassignedPanel
                  board={board}
                  onFocus={focusOrder}
                  onFindFit={(order) => {
                    setFitOrder(order);
                    setFitOpen(true);
                  }}
                  onPickUp={(order, x, y) =>
                    setPendingDrag({ orderId: order.id, label: order.name, x, y })
                  }
                />
              </div>

              {panelOpen ? (
                <Resizer
                  orientation="vertical"
                  onResize={panel.onResize}
                  label="Resize the actions panel"
                />
              ) : (
                <div />
              )}

              <div className="stage" style={{ gridTemplateRows: `1fr auto ${routes.size}px` }}>
                <div className="stage-map">
                  <RouteMap
                    board={board}
                    selectedRouteId={selectedRouteId}
                    highlightStopId={highlightStopId}
                    hiddenRouteIds={hiddenOnMap}
                    onSelectRoute={openRoute}
                    /* The same handler the timeline's chips use, so a stop
                     * answers a click identically whichever half it is in. */
                    onSelectStop={openStop}
                    onOpenOrder={(id, name) => setOpenOrder({ id, name })}
                    onUnassignOrder={(id, name) => void unassignFromMap(id, name)}
                    onDeleteOrder={(id, name) => void deleteFromMap(id, name)}
                    fitPreview={fitPreview}
                  />

                  {focusedRoute ? (
                    <div className="route-focus">
                      <b>{focusedRoute.technicianName ?? focusedRoute.vehicleName}</b>
                      <span>
                        {focusedRoute.stops.length} stops ·{' '}
                        {distance(focusedRoute.plannedDistanceM, board?.territory.distanceUnit)}
                      </span>
                      <button
                        type="button"
                        className="btn btn-small"
                        onClick={() => {
                          setSelectedRouteId(focusedRoute.id);
                          setHighlightStopId(null);
                          setDrawerOpen(true);
                        }}
                      >
                        Stops
                      </button>
                      <button
                        type="button"
                        className="btn btn-small"
                        onClick={() => setFocusRouteId(null)}
                      >
                        Show all
                      </button>
                    </div>
                  ) : null}
                </div>
                {/* Dragging down shrinks the routes grid beneath it. */}
                <Resizer
                  orientation="horizontal"
                  direction={-1}
                  onResize={routes.onResize}
                  onFit={fitRoutes}
                  label="Resize the routes grid"
                />

                <RoutesPanel
                  board={board}
                  selectedRouteId={selectedRouteId}
                  hiddenRouteIds={hiddenRoutes}
                  onSelectRoute={openRoute}
                  onSelectStop={openStop}
                  onOpenRoute={openRouteDrawer}
                  onToggleHidden={toggleHidden}
                  onToggleLock={(id, locked) => void toggleLock(id, locked)}
                  onLockAll={(locked) => void lockAll(locked)}
                  onHideAll={hideAll}
                  onMoveStop={moveStop}
                  onHoldStop={holdStop}
                  onDropMissed={(label) =>
                    ui.notify('info', `${label} stayed put`, 'Drop it on a technician row to place it.')
                  }
                  onDropUnchanged={(label, earliest) =>
                    ui.notify(
                      'info',
                      `${label} is already as early as it can be`,
                      earliest === undefined
                        ? undefined
                        : `The technician cannot reach it before ${hhmm(earliest)}. ` +
                          'Drag it later to hold it back; it cannot be pulled forward.',
                    )
                  }
                  /*
                   * A lock is a decision, so the way out is offered rather than
                   * just described -- one click unlocks the route that refused
                   * the drop.
                   */
                  onDropBlocked={(label, vehicleName, which) => {
                    const route = board?.routes.find((r) => r.vehicleName === vehicleName);
                    ui.notify(
                      'info',
                      `${vehicleName} is locked`,
                      which === 'source'
                        ? `${label} stays put. A locked route is held out of replans and out of edits.`
                        : `${label} was not placed there. Unlock ${vehicleName} to add work to that day.`,
                      route?.id
                        ? {
                            label: `Unlock ${vehicleName}`,
                            run: () => void toggleLock(route.id!, false),
                          }
                        : undefined,
                    );
                  }}
                  onShiftDay={shiftDay}
                  pendingDrag={pendingDrag}
                  onPendingDragTaken={() => setPendingDrag(null)}
                  onAssignOrder={assignOrder}
                  onFocusOrder={focusOrder}
                />
              </div>
            </div>
          </div>
        ) : null}

        {section === 'tracking' && !needsTerritory ? <TrackingView board={board} /> : null}

        {/*
          * The territory-scoped views below are keyed on the territory, so
          * switching one unmounts the view and mounts a fresh one.
          *
          * Every one of them loads in an effect and sets state from inside the
          * loader, so a slow response for the territory you just left could
          * land after a fast one for the territory you just chose and overwrite
          * it. Remounting makes that impossible rather than merely unlikely,
          * and costs one attribute against a guard in every loader in the app.
          */}
        {section === 'simulations' && !needsTerritory ? (
          <SimulationsView
            key={territoryId}
            territoryId={territoryId}
            date={date}
            onPromoted={() => void refresh()}
          />
        ) : null}

        {section === 'orders' && !needsTerritory ? (
          <OrdersView
            territoryId={territoryId}
            date={date}
            selectedRef={orderRef}
            onSelect={selectOrder}
          />
        ) : null}
        {section === 'fleet' ? (
          <SetupView
            key={territoryId}
            territoryId={territoryId}
            page={setupPage}
            onSelectPage={(page) => navigate({ setupPage: page })}
            onSelectTerritory={selectTerritory}
            onTerritoriesChanged={territoriesChanged}
          />
        ) : null}
        {section === 'account' ? (
          <div className="scroll-page">
            <AccountView
              territories={territories}
              tab={accountTab}
              onTab={(tab) => navigate({ section: 'account', accountTab: tab })}
            />
          </div>
        ) : null}
        {section === 'audit' ? (
          <div className="scroll-page">
            <AuditView territories={territories} />
          </div>
        ) : null}
        {section === 'billing' ? (
          <div className="scroll-page">
            <BillingView />
          </div>
        ) : null}
      </div>

      <FitInDrawer
        open={fitOpen}
        territoryId={territoryId}
        date={date}
        today={todayIn(timezone)}
        routes={board?.routes ?? []}
        order={fitOrder}
        onClose={() => {
          setFitOpen(false);
          setFitOrder(null);
        }}
        onPreview={setFitPreview}
      />

      <NewOrderDrawer
        open={newOrderOpen}
        territoryId={territoryId}
        date={date}
        onClose={() => setNewOrderOpen(false)}
        onCreated={() => void refresh()}
      />

      <RouteDrawer
        route={selectedRoute}
        unit={board?.territory.distanceUnit ?? 'mi'}
        territoryId={board?.territory.id ?? null}
        date={board?.date ?? ''}
        planVersion={board?.plan?.version ?? null}
        highlightStopId={highlightStopId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        /* Both act on a route that exists; an idle lane has none to reverse or lock. */
        onReverse={() => selectedRoute?.id && void reverseRoute(selectedRoute.id)}
        onToggleLock={() =>
          selectedRoute?.id && void toggleLock(selectedRoute.id, !selectedRoute.locked)
        }
        onOpenOrder={(id, name) => setOpenOrder({ id, name })}
        /*
         * The one place a replan can be asked for on a single driver.
         *
         * Scoped to this vehicle, so the solver reorders its stops and nobody
         * else's route is touched -- the same call the toolbar makes for the
         * whole fleet, with one id in it.
         */
        onReplan={
          selectedRoute ? () => void optimize([selectedRoute.vehicleId]) : undefined
        }
      />

      {/*
        * Stacked over the route, because that is where it was opened from and
        * that is what closing it should go back to.
        */}
      {territoryId ? (
        <OrderDrawer
          territoryId={territoryId}
          orderId={openOrder?.id ?? null}
          orderName={openOrder?.name ?? null}
          open={openOrder !== null}
          onClose={() => setOpenOrder(null)}
          onSaved={() => void refresh()}
          onRemoved={() => void refresh()}
          vehicles={orderDrawerVehicles}
        />
      ) : null}
    </div>
  );
}
